import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Elements } from "@stripe/react-stripe-js";
import { useLocation, useNavigate } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import AddCardModal from "../../components/AddCardModal";
import { getStripePromise } from "../../utils/stripeLoader";
import "../business/BusinessBilling.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL;
const stripePromise = getStripePromise(API_BASE_URL);

const FreelancerPaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const apiBaseUrl = API_BASE_URL;
  const isBoothRenterPath = location.pathname.includes("/booth-renter/");
  const backPath = isBoothRenterPath
    ? "/dashboard/booth-renter/marketing/sms-credits"
    : "/dashboard/freelancer/marketing/sms-credits";
  const roleLabel = isBoothRenterPath ? "booth renter" : "freelancer";

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      if (!token) {
        navigate("/login");
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${apiBaseUrl}/api/subscription-new/payment-methods`,
        { headers },
      );

      setPaymentMethods(response.data.paymentMethods || []);
      setError("");
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load payment methods",
      );
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, navigate]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleCardAdded = () => {
    setShowAddCardModal(false);
    fetchPaymentMethods();
  };

  const handleRemoveCard = async (paymentMethodId) => {
    if (
      !window.confirm("Are you sure you want to remove this payment method?")
    ) {
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(
        `${apiBaseUrl}/api/subscription-new/payment-methods/${paymentMethodId}`,
        { headers },
      );

      fetchPaymentMethods();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to remove payment method");
    } finally {
      setProcessing(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(
        `${apiBaseUrl}/api/subscription-new/payment-methods/${paymentMethodId}/default`,
        {},
        { headers },
      );

      fetchPaymentMethods();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "Failed to update default payment method",
      );
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <FreelancerSidebarLayout>
        <div className="billing-container">
          <div className="billing-header">
            <button className="btn-back" onClick={() => navigate(backPath)}>
              ← Back to SMS Credits
            </button>
            <h1>💳 Payment Methods</h1>
          </div>
          <div className="loading-state">Loading payment methods...</div>
        </div>
      </FreelancerSidebarLayout>
    );
  }

  return (
    <FreelancerSidebarLayout>
      <div className="billing-container">
        <div className="billing-header">
          <button className="btn-back" onClick={() => navigate(backPath)}>
            ← Back to SMS Credits
          </button>
          <h1>💳 Payment Methods</h1>
          <p>
            Manage the saved card used for {roleLabel} SMS credit purchases and
            auto-replenish.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="payment-methods-section">
          <div className="section-header">
            <h2>Saved Cards</h2>
            <button
              className="btn-add-card"
              onClick={() => setShowAddCardModal(true)}
              disabled={processing}
            >
              + Add New Card
            </button>
          </div>

          {paymentMethods.length > 0 ? (
            <div className="payment-methods-list">
              {paymentMethods.map((method) => (
                <div key={method.id} className="payment-method-card">
                  <div className="card-icon">{method.brand.toUpperCase()}</div>
                  <div className="card-details">
                    <div className="card-number">•••• {method.last4}</div>
                    <div className="card-expiry">
                      Expires {method.expMonth}/{method.expYear}
                    </div>
                  </div>
                  {method.isDefault && (
                    <span className="default-badge">Default</span>
                  )}
                  <div className="card-actions">
                    {!method.isDefault && (
                      <button
                        className="btn-action btn-set-default"
                        onClick={() => handleSetDefault(method.id)}
                        disabled={processing}
                        title="Set as default"
                      >
                        Set Default
                      </button>
                    )}
                    {paymentMethods.length > 1 && (
                      <button
                        className="btn-action btn-remove"
                        onClick={() => handleRemoveCard(method.id)}
                        disabled={processing}
                        title="Remove card"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No payment methods on file</p>
              <button
                className="btn-primary"
                onClick={() => setShowAddCardModal(true)}
              >
                Add Your First Card
              </button>
            </div>
          )}
        </div>

        {showAddCardModal && (
          <Elements stripe={stripePromise}>
            <AddCardModal
              onClose={() => setShowAddCardModal(false)}
              onSuccess={handleCardAdded}
              apiBaseUrl={apiBaseUrl}
            />
          </Elements>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
};

export default FreelancerPaymentMethods;